import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import { forwardRef, memo, type ButtonHTMLAttributes } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../../lib/cn';

export const iconButtonVariants = tv({
  base: 'focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm font-medium shadow-black/5 transition-all focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 hover:scale-110 active:scale-100 [&_svg]:stroke-[1.5]',
  variants: {
    variant: {
      primary:
        'bg-chat-primary text-chat-primary-foreground hover:bg-chat-primary/80 active:bg-chat-primary/50 shadow-sm',
      destructive:
        'bg-chat-destructive text-chat-destructive-foreground hover:bg-chat-destructive/80 active:bg-chat-destructive/50 shadow-sm',
      outline:
        'border-chat-input-border bg-chat-background hover:bg-chat-accent border shadow-sm text-chat-foreground',
      secondary:
        'bg-chat-secondary text-chat-secondary-foreground hover:bg-chat-secondary/80 active:bg-chat-secondary/50 shadow-sm',
      ghost: '',
    },
    size: {
      md: 'size-9 [&_svg]:size-4.5',
      xs: 'size-7 rounded-md [&_svg]:size-4',
      sm: 'size-8 rounded-md [&_svg]:size-4',
      lg: '[&_svg]:size-5 size-10 rounded-md',
      xl: 'size-11 rounded-md [&_svg]:size-6',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof iconButtonVariants>, 'outline'> {
  asChild?: boolean;
  loading?: boolean;
  circle?: boolean;
}

export const IconButton = memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        className,
        children,
        type = 'button',
        variant,
        size,
        asChild = false,
        loading = false,
        circle = false,
        ...restProps
      },
      ref,
    ) => {
      const Comp = asChild ? Slot.Root : 'button';
      const content = loading ? <Loader2 className='animate-spin' /> : children;

      return (
        <Comp
          type={asChild ? undefined : type}
          className={cn(
            iconButtonVariants({
              variant,
              size,
            }),
            circle && 'rounded-full',
            className,
          )}
          disabled={loading}
          ref={ref}
          {...restProps}
        >
          {content}
        </Comp>
      );
    },
  ),
);
